/**
 * New station page — same pattern as EmployeeCreate (CRUD template).
 * Route: /station/new (create) | /station/edit/:stationNumber (edit).
 * Reuses same form and addOrUpdateStation API for both.
 */
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import StationForm from './StationForm';
import PageContainer from '../mui-crud/components/PageContainer';
import * as api from '../../modules/api';
import {
  getDefaultGuidePhotoSlots,
  mapUrlsToGuidePhotoSlots,
  asJpgFile,
  guidePhotoIndex,
} from '../constants/guidePhotoTypes';
import {
  canAccessStation,
  isAdmin,
  loadAdminMerchantEmails,
} from '../utils/accessControl';
import {
  getDefaultWorkTime,
  isWorkTimeComplete,
  normalizeWorkTimeForForm,
} from '../utils/workTime';
import { geocodeAddress } from '../utils/geocodeAddress';
import { getApiErrorMessage } from '../utils/apiErrors';

const ROLES = { SUPER_ADMIN: 'SUPER_ADMIN', ADMIN: 'ADMIN', MERCHANT: 'MERCHANT' };

function getInitialValues(role, email) {
  const base = {
    stationID: '',
    stationNumber: '',
    address: '',
    additionalAddress: '',
    numberSlots: '',
    latitude: '',
    longitude: '',
    owner: role === ROLES.MERCHANT && email ? email : '',
    tariffPlanName: '',
    tariffPlanId: '',
    workTime: getDefaultWorkTime(),
  };
  return base;
}

/** Map API station object to form values for edit mode */
function stationToFormValues(station, role, email) {
  const st = station || {};
  const s = st.station || st;
  const workTime = st.workTime ?? s.workTime;
  const nSlots = st.numberSlots ?? s.numberSlots;
  const tariff = st.tariffPlan ?? s.tariffPlan ?? null;
  return {
    stationID: (st.stationId ?? st.stationID ?? s.stationId ?? s.stationID ?? '').toString().trim(),
    stationNumber: (st.stationNumber ?? s.stationNumber ?? '').toString().trim(),
    address: (st.address ?? s.address ?? '').toString().trim(),
    additionalAddress: (st.additionalAddress ?? s.additionalAddress ?? '').toString().trim(),
    numberSlots: nSlots != null && nSlots !== '' ? String(nSlots) : '',
    latitude: (st.latitude ?? s.latitude ?? '').toString().trim(),
    longitude: (st.longitude ?? s.longitude ?? '').toString().trim(),
    owner: (st.owner ?? s.owner ?? (role === ROLES.MERCHANT && email ? email : '')).toString().trim(),
    tariffPlanName: (tariff?.name ?? '').toString().trim(),
    // Mongo @DBRef requires a real tariff id — keep it from loaded station.
    tariffPlanId: (tariff?.id ?? '').toString().trim(),
    workTime: normalizeWorkTimeForForm(workTime),
  };
}

function normalizeTariffsList(raw) {
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const id = item.id != null ? String(item.id).trim() : '';
      const name = item.name != null ? String(item.name).trim() : '';
      if (!id || !name) return null;
      return { id, name, raw: item };
    })
    .filter(Boolean);
}

function validate(values, requireOwner, t) {
  const errors = {};
  const s = (v) => (v != null && v !== '' ? String(v).trim() : '');
  if (!s(values.address)) errors.address = t('stationPage.addressRequired');
  if (!s(values.additionalAddress)) errors.additionalAddress = t('stationPage.additionalAddressRequired');
  if (!s(values.stationNumber)) errors.stationNumber = t('stationPage.stationNumberRequired');
  if (!s(values.stationID)) errors.stationID = t('stationPage.stationIDRequired');
  if (requireOwner && !s(values.owner)) errors.owner = t('stationPage.selectMerchant');
  if (!s(values.tariffPlanName)) errors.tariffPlanName = t('stationPage.selectTariff');
  if (!isWorkTimeComplete(values.workTime)) {
    errors.workTime = t('stationPage.workTimeRequired');
  }
  const numSlots = values.numberSlots;
  if (numSlots === '' || numSlots == null) {
    errors.numberSlots = t('stationPage.numberSlotsRequired');
  } else {
    const n = Number(numSlots);
    if (!Number.isFinite(n) || n <= 0) errors.numberSlots = t('stationPage.numberSlotsInvalid');
  }
  return errors;
}

/** Normalize API response to array of email strings */
function normalizeMerchantsList(data) {
  if (!data) return [];
  const arr = Array.isArray(data) ? data : (data.data != null ? (Array.isArray(data.data) ? data.data : [data.data]) : []);
  return arr
    .map((item) => (typeof item === 'string' ? item : item?.email))
    .filter(Boolean);
}

export default function StationCreate() {
  const history = useHistory();
  const { t } = useTranslation();
  const { stationNumber: stationNumberParam } = useParams();
  const isEditMode = Boolean(stationNumberParam?.trim());
  const notifications = useNotifications();
  const role = typeof localStorage !== 'undefined' ? localStorage.getItem('role') || '' : '';
  const email = typeof localStorage !== 'undefined' ? localStorage.getItem('Email') || '' : '';

  const [formState, setFormState] = React.useState(() => ({
    values: getInitialValues(role, email),
    errors: {},
  }));
  const [merchantsList, setMerchantsList] = React.useState([]);
  const [tariffsList, setTariffsList] = React.useState([]);
  const [merchantsLoading, setMerchantsLoading] = React.useState(false);
  const [merchantsError, setMerchantsError] = React.useState(null);
  const [guidePhotos, setGuidePhotos] = React.useState(getDefaultGuidePhotoSlots);
  const [stationLoadError, setStationLoadError] = React.useState(null);
  const [stationLoading, setStationLoading] = React.useState(isEditMode);

  const formValues = formState.values;
  const formErrors = formState.errors;
  const ownerMode = role === ROLES.MERCHANT ? 'merchant' : role === ROLES.ADMIN ? 'admin' : 'super_admin';
  const requireOwner = ownerMode !== 'merchant';

  React.useEffect(() => {
    if (ownerMode === 'merchant') return;
    setMerchantsLoading(true);
    setMerchantsError(null);
    const promise =
      ownerMode === 'super_admin'
        ? api.getMerchants(true)
        : api.getMerchantsBy(email);
    promise
      .then((res) => {
        const data = res?.data;
        setMerchantsList(normalizeMerchantsList(data));
      })
      .catch((e) => {
        setMerchantsError(e?.message || t('stationPage.failedToLoadMerchants'));
        setMerchantsList([]);
      })
      .finally(() => setMerchantsLoading(false));
  }, [ownerMode, email]);

  React.useEffect(() => {
    let cancelled = false;
    api.GetAllGroup()
      .then((res) => {
        if (cancelled) return;
        setTariffsList(normalizeTariffsList(res?.data));
      })
      .catch(() => {
        if (!cancelled) setTariffsList([]);
      });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!isEditMode || !stationNumberParam) {
      setStationLoading(false);
      return;
    }
    let cancelled = false;
    setStationLoading(true);
    setStationLoadError(null);
    (async () => {
      try {
        const merchantEmails = isAdmin(role) ? await loadAdminMerchantEmails(email) : [];
        const res = await api.findStationByNumber(stationNumberParam);
        if (cancelled) return;
        const data = res?.data;
        const station = data != null ? (Array.isArray(data) ? data[0] : data) : null;
        if (!station) {
          setStationLoadError(t('stationPage.stationNotFound'));
          return;
        }
        if (!canAccessStation(station, { role, email, merchantEmails })) {
          setStationLoadError(t('access.stationEditForbidden'));
          notifications.show(t('access.stationEditForbidden'), { severity: 'warning', autoHideDuration: 4000 });
          history.replace('/station');
          return;
        }
        setFormState((prev) => ({
          ...prev,
          values: stationToFormValues(station, role, email),
        }));
      } catch (e) {
        if (!cancelled) setStationLoadError(t('stationPage.failedToLoadStation'));
      } finally {
        if (!cancelled) setStationLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, stationNumberParam, role, email, history, notifications, t]);

  React.useEffect(() => {
    if (!isEditMode || !stationNumberParam) return;
    api.getStationAllImg(stationNumberParam).then((res) => {
      const data = res?.data;
      setGuidePhotos(mapUrlsToGuidePhotoSlots(Array.isArray(data) ? data : []));
    }).catch(() => {});
  }, [isEditMode, stationNumberParam]);

  const handleFieldChange = React.useCallback((name, value) => {
    setFormState((prev) => {
      const nextValues = { ...prev.values, [name]: value };
      // Keep tariffPlanId in sync when user picks a tariff by name.
      if (name === 'tariffPlanName') {
        const match = tariffsList.find((t) => t.name === value);
        nextValues.tariffPlanId = match?.id || prev.values.tariffPlanId || '';
      }
      return {
        ...prev,
        values: nextValues,
      };
    });
  }, [tariffsList]);

  const setFormValues = React.useCallback((newFormValues) => {
    setFormState((prev) => ({ ...prev, values: newFormValues }));
  }, []);

  const setFormErrors = React.useCallback((newFormErrors) => {
    setFormState((prev) => ({ ...prev, errors: newFormErrors }));
  }, []);

  const handleFormFieldChange = React.useCallback(
    (name, value) => {
      let tariffPlanId = formValues.tariffPlanId;
      if (name === 'tariffPlanName') {
        tariffPlanId = tariffsList.find((t) => t.name === value)?.id || formValues.tariffPlanId || '';
      }
      handleFieldChange(name, value);
      const newValues = {
        ...formValues,
        [name]: value,
        ...(name === 'tariffPlanName' ? { tariffPlanId } : {}),
      };
      const errs = validate(newValues, requireOwner, t);
      setFormErrors({ ...formErrors, [name]: errs[name] || null });
    },
    [formValues, formErrors, handleFieldChange, setFormErrors, requireOwner, t, tariffsList],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(getInitialValues(role, email));
    setFormErrors({});
  }, [setFormValues, setFormErrors, role, email]);

  const handleAddressSelect = React.useCallback(
    (suggestion) => {
      if (!suggestion) return;
      const address = suggestion.value ?? suggestion.address ?? '';
      const latitude = suggestion.data?.geo_lat ?? suggestion.latitude ?? '';
      const longitude = suggestion.data?.geo_lon ?? suggestion.longitude ?? '';
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          address,
          latitude,
          longitude,
        },
      }));
    },
    [],
  );

  const handleWorkTimeChange = React.useCallback((day, value) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        workTime: {
          ...(prev.values.workTime || getDefaultWorkTime()),
          [day]: value,
        },
      },
    }));
  }, []);

  const handleFormSubmit = React.useCallback(async () => {
    const errors = validate(formValues, requireOwner, t);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const tariffName = String(formValues.tariffPlanName ?? '').trim();
    const matchedTariff =
      tariffsList.find((t) => t.name === tariffName) ||
      tariffsList.find((t) => t.id === String(formValues.tariffPlanId || '').trim());
    const tariffId = (matchedTariff?.id || formValues.tariffPlanId || '').toString().trim();
    if (!tariffId) {
      setFormErrors({ tariffPlanName: t('stationPage.selectTariff') });
      return;
    }
    setFormErrors({});

    // Backend persists tariffPlan as Mongo @DBRef — id is required.
    const tariffPlan = {
      id: tariffId,
      name: matchedTariff?.name || tariffName,
    };
    const workTime = normalizeWorkTimeForForm(formValues.workTime);
    const address = (formValues.address ?? '').trim();
    const additionalAddress = (formValues.additionalAddress ?? '').trim();
    const stationNumber = (formValues.stationNumber ?? '').trim();
    const stationID = (formValues.stationID ?? '').trim();
    const numberSlots = formValues.numberSlots === '' ? undefined : Number(formValues.numberSlots);
    const owner = (formValues.owner ?? '').trim();
    // Backend AddStationRequest expects latitude/longitude as strings.
    let lat = formValues.latitude === '' || formValues.latitude == null ? '' : String(formValues.latitude);
    let lng = formValues.longitude === '' || formValues.longitude == null ? '' : String(formValues.longitude);
    if (!lat || !lng) {
      try {
        const geo = await geocodeAddress([address, additionalAddress].filter(Boolean).join(', '));
        lat = geo.latitude;
        lng = geo.longitude;
        setFormState((prev) => ({
          ...prev,
          values: { ...prev.values, latitude: lat, longitude: lng },
        }));
      } catch (_) {
        setFormErrors({ address: t('stationPage.coordsRequired') });
        notifications.show(t('stationPage.coordsRequired'), { severity: 'error', autoHideDuration: 6000 });
        return;
      }
    }

    try {
      await api.addStation(
        tariffPlan,
        address,
        lat,
        lng,
        numberSlots,
        owner,
        stationID,
        stationNumber,
        workTime,
        additionalAddress,
      );
    } catch (err) {
      const data = err?.response?.data;
      const code = typeof data === 'string' ? data.replace(/^"+|"+$/g, '') : (data?.errorCode || data?.code || '');
      const byCode = {
        LATITUDE_IS_EMPTY: t('stationPage.coordsRequired'),
        LONGITUDE_IS_EMPTY: t('stationPage.coordsRequired'),
        ADDRESS_IS_EMPTY: t('stationPage.addressRequired'),
        ADDITIONAL_ADDRESS_IS_EMPTY: t('stationPage.additionalAddressRequired'),
        STATION_ID_IS_EMPTY: t('stationPage.stationIDRequired'),
        STATION_NUMBER_IS_EMPTY: t('stationPage.stationNumberRequired'),
        NUMBER_SLOTS_IS_EMPTY: t('stationPage.numberSlotsRequired'),
        WORK_TIME_IS_EMPTY: t('stationPage.workTimeRequired'),
      };
      const message =
        byCode[code] ||
        getApiErrorMessage(err, t) ||
        (/network error/i.test(String(err?.message || '')) ? t('stationPage.networkError') : null) ||
        (isEditMode ? t('stationPage.updateError') : t('stationPage.createError'));
      notifications.show(message, { severity: 'error', autoHideDuration: 6000 });
      throw err;
    }

    const newPhotos = guidePhotos.filter((p) => p.file != null);
    if (newPhotos.length > 0) {
      try {
        const files = newPhotos.map((p) => {
          const idx = guidePhotoIndex(p.type) || 1;
          return asJpgFile(p.file, `${stationNumber}_${idx}.jpg`);
        });
        const types = newPhotos.map((p) => p.type);
        await api.AddImage(files, stationNumber, types);
      } catch (photoErr) {
        notifications.show(
          isEditMode ? t('stationPage.updateSuccessPhotosFailed') : t('stationPage.createSuccessPhotosFailed'),
          { severity: 'warning', autoHideDuration: 6000 },
        );
        history.push(`/station/edit/${encodeURIComponent(stationNumber)}`);
        return;
      }
    }

    notifications.show(
      isEditMode ? t('stationPage.updateSuccess') : t('stationPage.createSuccess'),
      { severity: 'success', autoHideDuration: 3000 },
    );
    history.push('/station');
  }, [formValues, guidePhotos, history, notifications, setFormErrors, requireOwner, isEditMode, t, tariffsList]);

  return (
    <PageContainer
      title={isEditMode ? t('stationPage.titleEdit') : t('stationPage.titleNew')}
      breadcrumbs={[
        { title: t('stationPage.breadcrumbStations'), path: '/station' },
        { title: isEditMode ? (stationNumberParam || t('stationPage.breadcrumbEdit')) : t('stationPage.breadcrumbNew') },
      ]}
    >
      {stationLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {stationLoadError && !stationLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stationLoadError}
        </Alert>
      )}
      {merchantsError && !stationLoadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {merchantsError} {t('stationPage.merchantsErrorSuffix')}
        </Alert>
      )}
      {!stationLoading && !stationLoadError && (
        <StationForm
          formState={formState}
          onFieldChange={handleFormFieldChange}
          onSubmit={handleFormSubmit}
          onReset={handleFormReset}
          submitButtonLabel={isEditMode ? t('stationPage.save') : t('stationPage.create')}
          backButtonPath="/station"
          ownerMode={ownerMode}
          merchantsList={merchantsList}
          tariffsList={tariffsList}
          onAddressSelect={handleAddressSelect}
          onWorkTimeChange={handleWorkTimeChange}
          guidePhotos={guidePhotos}
          onGuidePhotosChange={setGuidePhotos}
          minPhotos={0}
        />
      )}
    </PageContainer>
  );
}
